/* What the map may do to a cell, and why not.

One place that mirrors the server's refusals (`simulator/use_cases/*`) before the click, so a button
that can be pressed is an order the server will take. Pure: the action bar asks this, tests pin it.

Every entry: { key, enabled, reason } — `reason` is an i18n key under game.mapAction.reason (plus
params) and is null when enabled. Actions that do not apply to the cell at all are simply absent. */

import {
  colonyPriceAt,
  coloniesHeld,
  foundingOrderedAt,
  pendingFoundingsOf,
  startingCitiesOf,
  upgradeOrders,
} from "src/utils/pendingOrders"
import constants from "src/constants"
import {
  maxActions as maxActionsFor,
  siloRange,
  tunnelContents,
  tunnelLoadCapacity,
  tunnelNetworkCapacity,
} from "src/utils/gameModifiers"
import { detectionRadius } from "src/utils/lookouts"
import { teamsPresentAt } from "src/utils/siege"
import { columnDistance } from "src/utils/world"

/* Orthogonal steps the short way round the world: columns wrap (2026-09-05), the same rule the
   server's `distance` applies to every range it checks. */
export const manhattan = (a, b) => columnDistance(a.col, b.col) + Math.abs(a.row - b.row)

const unitsIn = (metadata) =>
  Object.values(metadata?.units || {}).reduce((total, unit) => total + (unit.number || 0), 0)

const cellKey = (coords) => `${coords.col}-${coords.row}`
const parseKey = (key) => {
  const [col, row] = key.split("-").map(Number)
  return { col, row }
}

// Base radius plus the detection line, the way `detection_radius` does it server-side: thermal
// suits for humanity, the stalker for the horde. It read the human line for both, so a zombie's
// ring was drawn a cell short of what the server granted (found 2026-09-06).
export { detectionRadius }

/* The vision ring for a selected cell — `{col, row, radius}` for the canvas's range wash, or null
   when there is no seat to look from. Every cell has one (owner, 2026-09-06): the ring used to
   need the player's own units on the cell or a teammate's deed, and a cell that showed no ring
   read as "nothing to see here" rather than "not yours". Whose radius: a teammate's city sees with
   the teammate's research (their farms, their thermal suits); the viewer's own units anywhere, an
   enemy city, or empty ground are all read with the viewer's — "what would I see from here". */
export const visionRangeFor = ({ selected, cells, users, userId, rules }) => {
  if (!selected) return null
  const mine = users?.[String(userId)]
  if (!mine) return null
  const cell = cells?.[`${selected.col}-${selected.row}`]
  const ownUnitsHere = unitsIn(cell?.metadata_by_user?.[String(userId)]) > 0
  const owner = cell?.owner == null ? null : users?.[String(cell.owner)]
  const seesWith = !ownUnitsHere && owner && owner.team === mine.team ? owner : mine
  return { col: Number(selected.col), row: Number(selected.row), radius: detectionRadius(seesWith, rules) }
}

/* How far a silo reaches. S13 (2026-09-05): a flat base plus research, no longer × level — the
   level buys damage now; U6 (2026-09-07): the research is the missile program's own levels past
   the first. `siloRange` is the one mirror of the server's `modifiers.silo_range`, so this is a
   thin wrapper and not a second formula. */
export const strikeRange = (silo, gameConfig, researchLevels) =>
  (silo?.level ?? 0) > 0 ? siloRange({ gameConfig, researchLevels, level: silo.level }) : 0

/* What the next colony costs this player, mirroring the server's `found_settlement.settlement_cost`
   (owner, 2026-09-05): the price climbs with every colony already held — a colony being any owned
   cell that is not one of the player's initial crowns (`utils/pendingOrders.coloniesHeld`). The
   rule and the constants both come from `GET /game-config/`. */
const priceAt = colonyPriceAt

/* `pending` is this player's colonies ordered this round and not yet founded (owner, 2026-10-02):
   the server counts them as held, so the second colony ordered in a round is quoted at the second
   colony's price (`utils/pendingOrders.pendingFoundingsOf`). */
export const settlementCostFor = ({ rules, cells, userId, team, initialLocations, pending = 0 }) =>
  priceAt(rules, team, coloniesHeld({ cells, userId, initialLocations }) + pending)

/* What a pending order changes about a level at (col, row) — the `ordered` that `upgradeGate`
   takes — read off the game the client holds: this building's level already ordered this round.
   The purse it is priced against is already the free one (`pendingOrders.effectiveForPlayer`).
   The one door the city sheet, the building sheet and the roster go through, so the three cannot
   disagree. */
export const upgradeOrdersFor = ({ game, userId, col, row, buildingCode }) =>
  upgradeOrders({ metadata: game?.metadata, userId, col, row, buildingCode })

/* The founding party a Colonize press sends (owner, 2026-09-07): humanity's deed is paid in
   workers standing on the target, so exactly the price walks; the horde pays meat from the pool
   on arrival and only needs a body standing there, so one zombie walks — the plain `zombie` if
   the city has one, else one of whatever is most numerous. `{type: number}`, the shape the troop
   picker writes, or null when the city cannot field the party. */
export const colonizingPartyFor = ({ team, units, cost }) => {
  if (team === "human") {
    const workers = Number(units?.[constants.units.human.WORKER]?.number ?? 0)
    return cost > 0 && workers >= cost ? { [constants.units.human.WORKER]: cost } : null
  }
  const present = Object.entries(units || {}).filter(([, unit]) => (unit?.number ?? 0) > 0)
  if (present.length === 0) return null
  const basic = present.find(([type]) => type === constants.units.zombie.ZOMBIE)
  const [type] = basic || present.sort((a, b) => (b[1].number ?? 0) - (a[1].number ?? 0))[0]
  return { [type]: 1 }
}

export const cellActions = ({
  coords,
  cellMetadata,
  userId,
  userMetadata,
  cells,
  users,
  rules,
  gameConfig,
  tick,
  isLand = true,
  movementsHere = 0,
  movements = [],
  battles = {},
  upgrades = [],
  foundings = [],
  tunnelOrders = [],
}) => {
  const team = userMetadata?.team
  // Orders of this round that land when it turns (owner, 2026-10-02): this player's colonies are
  // priced as held, and the horde's bank is what is left once its orders are paid.
  const orders = { upgrades, foundings, cells }
  const pendingColonies = pendingFoundingsOf({ metadata: orders, userId })
  const maxActions = maxActionsFor({ gameConfig: { rules_config: rules }, race: userMetadata?.race })
  const actionsLeft = Math.max(0, maxActions - Number(userMetadata?.actions_used ?? 0))
  const noActions = actionsLeft === 0 ? { key: "noActions" } : null

  const mine = cellMetadata?.metadata_by_user?.[String(userId)]
  const ownUnits = unitsIn(mine)
  const ownerId = cellMetadata?.owner ?? null
  const isOwn = ownerId != null && String(ownerId) === String(userId)

  // Stacks *standing* on this very cell — an attacker held at the gates of a defended cell stays
  // a movement now (2026-08-27), so a battle's second team may live entirely in the movements
  // list, with its units (36/V1).
  const standingStacks = (movements || []).filter((movement) => {
    const path = movement.path || []
    if (!path.length) return false
    const index = Math.min(movement.current_index ?? 0, path.length - 1)
    const [column, row] = path[index]
    if (column !== Number(coords.col) || row !== Number(coords.row)) return false
    return Object.values(movement.units || {}).some((unit) => (unit?.number ?? 0) > 0)
  })
  // Bodies of the player's own stacks on the cell — fighting at the gates or stopped on the way
  // (owner's R26): "troops here" has to count them, or a city with 300 soldiers at its gates
  // reads as empty.
  const ownStandingUnits = standingStacks
    .filter((movement) => movement.user_id === userId)
    .reduce(
      (total, movement) =>
        total + Object.values(movement.units || {}).reduce((sum, unit) => sum + (unit?.number ?? 0), 0),
      0
    )

  // A battle burning here: both teams hold troops on the cell — garrisoned or at the gates.
  // Computed before the orders below rather than beside the BATTLE button it also lights, because
  // it is what refuses a march out of the cell (owner, 2026-09-09) — troops in a fight hold the
  // ground or fall on it. `move_troops` refuses the same order with `CannotMarchFromACombat`;
  // this is the courtesy that says so before the press costs an action and the road's provisions.
  // One definition of who is on a cell, shared with the city sheet and the food projection —
  // `utils/siege` exists so "in combat" cannot mean three different things on three screens. The
  // cell is handed in as `cellMetadata` rather than looked up, because a caller may be asking
  // about ground that is not in `cells` at all.
  const teamsPresent = teamsPresentAt({
    coords: { column: Number(coords.col), row: Number(coords.row) },
    cells: { [cellKey(coords)]: cellMetadata },
    users,
    movements,
  })
  const underSiege = teamsPresent.size > 1

  const actions = []
  const push = (key, enabled, reason = null, extra = {}) => actions.push({ key, enabled, reason, ...extra })

  // Garrisons the player could send from: every cell where they have troops, nearest first.
  const origins = Object.entries(cells || {})
    .map(([key, cell]) => ({ ...parseKey(key), units: unitsIn(cell?.metadata_by_user?.[String(userId)]) }))
    .filter((origin) => origin.units > 0 && !(origin.col === coords.col && origin.row === coords.row))
    .sort((a, b) => manhattan(a, coords) - manhattan(b, coords))

  // March: from a cell you stand on. Offered on your own cells even when empty, so an empty city
  // says "no troops here" rather than hiding the order.
  if (ownUnits > 0 || isOwn) {
    const reason = noActions || (ownUnits === 0 ? { key: "noTroops" } : null)
    // Troops sharing the cell with an enemy are fighting, and a fight is not something you walk
    // out of. Infiltration used to be the exception on both sides of that seam; it was removed
    // from the game on 2026-09-10, so the siege holds everyone.
    push("march", !reason && !underSiege, underSiege ? { key: "siegeNoMarch" } : reason)
  }

  // Colonize (owner, 2026-09-07): from your own city, the founding party in one press — the
  // deed's workers for humanity, one body for the horde — and straight on to drawing the route,
  // the picker skipped. A helper and nothing more: the march is an ordinary `move_troops`, and
  // on arrival the player still presses Settle, because founding is an action of its own and a
  // stack in the field cannot pay for the deed. The price quoted is the one Settle will charge;
  // the horde's meat is not checked here, since the pool has a march's worth of rounds to grow.
  if (isOwn) {
    const cost = settlementCostFor({
      rules,
      cells,
      userId,
      team,
      initialLocations: startingCitiesOf(userMetadata),
      pending: pendingColonies,
    })
    const troops = colonizingPartyFor({ team, units: mine?.units, cost })
    // The founding party is an ordinary `move_troops`, so it is refused out of a fight like any
    // other march — and a city under siege has more pressing work than founding a colony.
    let reason = noActions || (underSiege ? { key: "siegeNoMarch" } : null)
    if (team === "human") {
      const workersHere = Number(mine?.units?.worker?.number ?? 0)
      reason = reason || (!troops ? { key: "needFoundingWorkers", cost, workers: workersHere } : null)
    } else {
      reason = reason || (!troops ? { key: "noTroops" } : null)
    }
    push("colonize", !reason, reason, { cost, currency: team === "human" ? "workers" : "meat", troops })
  }

  // March *to* this cell from one of those garrisons — how new ground is taken. Offered wherever
  // the player has nothing standing (otherwise "march" from here covers it).
  if (ownUnits === 0 && !isOwn && isLand) {
    const reason = noActions || (origins.length === 0 ? { key: "noArmies" } : null)
    push("marchTo", !reason, reason, { origins })
  }

  // Founding: troops on unowned land, the deed's price in the team's own coin (Q23/Q28), an
  // action. The horde pays meat from the pool; humanity pays workers *standing on the cell* —
  // the expedition has to bring its founders.
  if (ownerId == null && ownUnits > 0) {
    // Not while the ground is still being fought over (owner, 2026-09-09). Founding turns a stack
    // into a city, and the square's owner is not decided until the battle on it is; the server
    // refuses it as `CannotSettleWhileFighting`.
    let reason = !isLand ? { key: "notLand" } : underSiege ? { key: "siegeNoSettle" } : noActions
    // One colony a cell a round (`SettlementAlreadyOrdered`): it is founded when the round turns.
    const ordered = foundingOrderedAt({ metadata: orders, col: coords.col, row: coords.row })
    reason = reason || (ordered ? { key: "settleOrdered" } : null)
    let cost
    let currency
    const initialLocations = startingCitiesOf(userMetadata)
    const settleCost = settlementCostFor({ rules, cells, userId, team, initialLocations, pending: pendingColonies })
    if (team === "human") {
      cost = settleCost
      currency = "workers"
      const workersHere = Number(mine?.units?.worker?.number ?? 0)
      reason = reason || (workersHere < cost ? { key: "needFoundingWorkers", cost, workers: workersHere } : null)
    } else {
      cost = settleCost
      currency = "meat"
      // The bank is already the free one: what the orders given hold is off it (`effectiveForPlayer`).
      const food = Number(userMetadata?.food ?? 0)
      reason = reason || (food < cost ? { key: "needFood", cost, food } : null)
    }
    push("settle", !reason, reason, { cost, currency, ordered: Boolean(ordered) })
    // A colony of ours ordered here can be taken back until the round turns (owner, 2026-10-02:
    // "let's allow cancel"). The hold is released and the action comes back (2026-10-05).
    if (ordered && String(ordered.user_id) === String(userId)) push("cancelSettle", true, null)
  }

  // The view opens regardless of the action budget — looking costs nothing. The engine's own clock
  // (`metadata.battles`) counts too: the city sheet read both until its BATTLE button went to this
  // bar (owner, 2026-09-30), and a fight still on the clock with one side gone from the square is a
  // fight whose report the player may still want.
  if (underSiege || Number(battles?.[cellKey(coords)] ?? 0) > 0) push("battle", true)

  // Strike: a silo on this very cell, loaded.
  const silo = mine?.buildings?.[constants.buildings.human.MISSILE_SILO]
  if (team === "human" && (silo?.level ?? 0) > 0) {
    // The programme is what arms it (2026-08-21): a silo that came with a conquered city is a
    // building, not a weapon, until its owner has done the research.
    const hasProgram = Number(userMetadata?.researches?.missile_program ?? 0) > 0
    const reloadRounds = Math.max(0, (silo.cooldown_until_tick ?? 0) - (tick ?? 0))
    const reason =
      (!hasProgram && { key: "noMissileProgram" }) ||
      noActions ||
      (reloadRounds > 0 ? { key: "siloReloading", count: reloadRounds } : null)
    push("strike", !reason, reason, { range: strikeRange(silo, gameConfig, userMetadata?.researches) })
  }

  // The tunnel (owner, 2026-09-29; the Red Nydus's jump until then): two orders on a tunnel city
  // of yours, mirroring `use_cases/tunnel.py`, each one action since 2026-09-30. LOAD puts units
  // from this garrison into your one network — at most this city's entry cap, never past the
  // network's limit (the tunnel program's level), never from a city in a fight (a besieged
  // garrison fights; there is no retreat since 2026-09-08, U12). UNLOAD brings any part of the
  // network out into this garrison, a fight here or not — so the cell pressed IS the target, the
  // way the silo's press aims at the cell it is fired at. A captured tunnel without the program is
  // refused server-side (`TunnelProgramRequired`), the same rule as the silo; this is the courtesy
  // that says why before the click.
  const tunnel = mine?.buildings?.[constants.buildings.zombie.TUNNEL]
  if (team === "zombie" && (tunnel?.level ?? 0) > 0) {
    const limit = tunnelNetworkCapacity({ gameConfig, researchLevels: userMetadata?.researches })
    const hasTunnelProgram = Number(userMetadata?.researches?.tunnel_program ?? 0) > 0
    const inTunnel = tunnelContents(userMetadata?.tunnel)
    // Every tunnel order of this round counts against the limit until the round turns (owner,
    // 2026-10-05): an entry is not in yet and an exit not out yet, so both hold room, and the
    // network shown is already without the exits (`pendingOrders.effectiveForPlayer`).
    const ordered = (tunnelOrders || [])
      .filter((order) => String(order.user_id) === String(userId))
      .reduce((sum, order) => sum + Object.values(order.units || {}).reduce((a, n) => a + Number(n || 0), 0), 0)
    const room = Math.max(0, limit - inTunnel - ordered)
    const loadReason =
      (!hasTunnelProgram && { key: "noTunnelProgram" }) ||
      (underSiege && { key: "siegeNoTunnel" }) ||
      (ownUnits === 0 && { key: "noTroops" }) ||
      noActions ||
      (room === 0 ? { key: "tunnelFull" } : null)
    push("tunnelLoad", !loadReason, loadReason, {
      capacity: tunnelLoadCapacity({ gameConfig, level: tunnel.level }),
      inTunnel,
      limit,
      room,
    })
    const unloadReason =
      (!hasTunnelProgram && { key: "noTunnelProgram" }) || noActions || (inTunnel === 0 ? { key: "tunnelEmpty" } : null)
    push("tunnelUnload", !unloadReason, unloadReason, { inTunnel })
  }

  // DAR DE BAJA (owner, 2026-09-30): humanity turns this cell's units into workers on it, one a
  // body of flesh and none for a machine, mirroring `use_cases/discharge_units.py`. Offered on your
  // own city **or on ground nobody owns** (owner, 2026-10-02: "it should not matter [whether it is
  // a base]. So I can build a new base from soldiers"), wherever you hold anything but workers; an
  // enemy's or an ally's city is refused (`CellNotOwned`). It costs no action, so the budget never
  // refuses it, and a fight does (`CellUnderSiege` — a stack in a fight fights where it stands).
  // No ceiling limits it anywhere: the population ceiling stops a command centre's growth and
  // never deletes a worker (owner, 2026-10-02). `held` only words the sheet's title. An order
  // since 2026-10-23, made when the round turns: the units it holds are already off `mine` here
  // (`pendingOrders.effectiveForPlayer`), so a cell whose every body is retiring offers nothing.
  const dischargeable = Object.entries(mine?.units || {}).some(
    ([type, unit]) => type !== constants.units.human.WORKER && (unit?.number ?? 0) > 0
  )
  if (team === "human" && (isOwn || ownerId == null) && dischargeable) {
    const reason = underSiege ? { key: "siegeNoDischarge" } : null
    push("discharge", !reason, reason, { held: isOwn })
  }

  // The city view has something to show when a city stands here (anyone's — an enemy's shows what
  // fog allows), the player's own troops stand on unowned ground (the founding view), or a march
  // touches the cell (its details are the info; 2026-08-25). Truly empty land has nothing to open.
  // Anybody's troops, not only the player's own (owner's R10): a teammate's camp on unclaimed
  // ground is worth opening — the sheet lists who stands there.
  const anyoneHere = Object.values(cellMetadata?.metadata_by_user || {}).some((metadata) =>
    Object.values(metadata?.units || {}).some((unit) => (unit?.number ?? 0) > 0)
  )
  // Absent rather than greyed out when there is nothing to open (owner, 2026-09-07): a disabled
  // CITY on empty ground was a button announcing a door that is not there. Every other entry
  // stays disabled-with-a-reason because its door exists and the reason is the information.
  const hasCity = ownerId != null || ownUnits > 0 || anyoneHere || movementsHere > 0
  if (hasCity) push("city", true, null)

  return { actions, actionsLeft, ownUnits, ownStandingUnits, ownerId, isOwn, origins, cellKey: cellKey(coords) }
}

/* The two tunnel orders at one city — `{tunnelLoad, tunnelUnload}`, each the entry `cellActions`
   pushes, or absent where the city has no tunnel of the viewer's — read off a whole game payload.
   The city sheet's ENTRAR / SALIR and the tunnel sheet's confirm ask this, so both answer with the
   map bar's own rules and reasons rather than a second copy of them. */
export const tunnelOrdersAt = ({ city, game, gameConfig, userId }) => {
  if (!city) return {}
  const cells = game?.metadata?.cells || {}
  const users = game?.metadata?.users || {}
  const { actions } = cellActions({
    coords: city,
    cellMetadata: cells[cellKey(city)],
    userId,
    userMetadata: users[String(userId)],
    cells,
    users,
    rules: gameConfig?.rules_config,
    gameConfig,
    tick: game?.tick,
    movements: game?.metadata?.movements,
    tunnelOrders: game?.metadata?.tunnel_orders,
  })
  return Object.fromEntries(
    actions.filter((action) => action.key === "tunnelLoad" || action.key === "tunnelUnload").map((action) => [action.key, action])
  )
}

/* The discharge order at one cell — a city of the viewer's or open ground — the entry `cellActions`
   pushes, or null where the viewer has none there. The city sheet's DAR DE BAJA and the discharge sheet's confirm read it, so both answer
   with the map bar's own rule and reason. */
export const dischargeOrderAt = ({ city, game, gameConfig, userId }) => {
  if (!city) return null
  const cells = game?.metadata?.cells || {}
  const users = game?.metadata?.users || {}
  const { actions } = cellActions({
    coords: city,
    cellMetadata: cells[cellKey(city)],
    userId,
    userMetadata: users[String(userId)],
    cells,
    users,
    rules: gameConfig?.rules_config,
    gameConfig,
    tick: game?.tick,
    movements: game?.metadata?.movements,
  })
  return actions.find((action) => action.key === "discharge") || null
}
